import React, { useState } from 'react';
import { X, AlertTriangle, ChevronDown, ChevronRight, ChevronLeft, Clock, Users, CheckSquare } from 'lucide-react';
import { RecentlyCheckedDriver } from '../../services/api';

interface RecentCheckWarningModalProps {
    isOpen: boolean;
    onClose: () => void;
    recentlyCheckedDrivers: RecentlyCheckedDriver[];
    driversNeedingCheck: RecentlyCheckedDriver[];
    onProceed: (selectedDriverIds: string[]) => void;
    thresholdDays: number;
}

const ITEMS_PER_PAGE = 20;

const RecentCheckWarningModal: React.FC<RecentCheckWarningModalProps> = ({
    isOpen,
    onClose,
    recentlyCheckedDrivers,
    driversNeedingCheck,
    onProceed,
    thresholdDays
}) => {
    const [selectedRecentDrivers, setSelectedRecentDrivers] = useState<Set<string>>(new Set());

    // Collapse state - Recently Checked collapsed by default, Ready to Check expanded
    const [recentlyCheckedExpanded, setRecentlyCheckedExpanded] = useState(false);
    const [readyToCheckExpanded, setReadyToCheckExpanded] = useState(true);

    // Pagination state
    const [recentlyCheckedPage, setRecentlyCheckedPage] = useState(1);
    const [readyToCheckPage, setReadyToCheckPage] = useState(1);

    if (!isOpen) return null;

    // Calculate paginated data
    const recentlyCheckedTotalPages = Math.ceil(recentlyCheckedDrivers.length / ITEMS_PER_PAGE);
    const readyToCheckTotalPages = Math.ceil(driversNeedingCheck.length / ITEMS_PER_PAGE);

    const paginatedRecentlyChecked = recentlyCheckedDrivers.slice(
        (recentlyCheckedPage - 1) * ITEMS_PER_PAGE,
        recentlyCheckedPage * ITEMS_PER_PAGE
    );

    const paginatedReadyToCheck = driversNeedingCheck.slice(
        (readyToCheckPage - 1) * ITEMS_PER_PAGE,
        readyToCheckPage * ITEMS_PER_PAGE
    );

    const handleToggleDriver = (driverId: string) => {
        const newSelected = new Set(selectedRecentDrivers);
        if (newSelected.has(driverId)) {
            newSelected.delete(driverId);
        } else {
            newSelected.add(driverId);
        }
        setSelectedRecentDrivers(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedRecentDrivers.size === recentlyCheckedDrivers.length) {
            setSelectedRecentDrivers(new Set());
        } else {
            setSelectedRecentDrivers(new Set(recentlyCheckedDrivers.map(d => d.id)));
        }
    };

    const handleProceed = () => {
        // Include all drivers needing check + selected recently-checked drivers
        const driverIdsToCheck = [
            ...driversNeedingCheck.map(d => d.id),
            ...Array.from(selectedRecentDrivers)
        ];
        onProceed(driverIdsToCheck);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const totalDriversToCheck = driversNeedingCheck.length + selectedRecentDrivers.size;

    // Calculate summary stats for collapsed headers
    const recentlyCheckedValidCount = recentlyCheckedDrivers.filter(d => d.licenceValid).length;
    const recentlyCheckedInvalidCount = recentlyCheckedDrivers.length - recentlyCheckedValidCount;

    const readyToCheckNeverChecked = driversNeedingCheck.filter(d => !d.lastCheckDate).length;
    const readyToCheckFailed = driversNeedingCheck.filter(d => d.checkSucceeded === false).length;

    // Pagination controls component
    const PaginationControls = ({
        currentPage,
        totalPages,
        onPageChange,
        totalItems
    }: {
        currentPage: number;
        totalPages: number;
        onPageChange: (page: number) => void;
        totalItems: number;
    }) => {
        if (totalPages <= 1) return null;

        const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
        const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

        return (
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                    Showing {startItem}-{endItem} of {totalItems}
                </span>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <span className="text-xs text-gray-500">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                        <div className={`p-2.5 rounded-lg ${recentlyCheckedDrivers.length > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                            {recentlyCheckedDrivers.length > 0
                                ? <AlertTriangle className="h-5 w-5 text-amber-500" />
                                : <CheckSquare className="h-5 w-5 text-green-600" />
                            }
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                {recentlyCheckedDrivers.length > 0 ? 'Recently Checked Drivers Found' : 'Confirm Licence Check'}
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {recentlyCheckedDrivers.length > 0
                                    ? `${recentlyCheckedDrivers.length} driver${recentlyCheckedDrivers.length !== 1 ? 's were' : ' was'} successfully checked in the last ${thresholdDays} days`
                                    : `${driversNeedingCheck.length} driver${driversNeedingCheck.length !== 1 ? 's' : ''} will be checked`
                                }
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {/* Summary Stats - Compact inline */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-6 text-sm">
                            <div className="flex items-center space-x-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#69BE28' }}></div>
                                <span className="text-gray-600">Ready to check:</span>
                                <span className="font-semibold text-gray-900">{driversNeedingCheck.length}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
                                <span className="text-gray-600">Recently checked:</span>
                                <span className="font-semibold text-gray-900">{recentlyCheckedDrivers.length}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">Total to check:</span>
                                <span className="font-semibold text-gray-900">{totalDriversToCheck}</span>
                            </div>
                        </div>
                    </div>

                    {/* Recently Checked Drivers Section - Collapsible Accordion */}
                    {recentlyCheckedDrivers.length > 0 && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Accordion Header */}
                            <button
                                onClick={() => setRecentlyCheckedExpanded(!recentlyCheckedExpanded)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <div className="flex items-center space-x-3">
                                    {recentlyCheckedExpanded ? (
                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                    ) : (
                                        <ChevronRight className="w-5 h-5 text-gray-500" />
                                    )}
                                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
                                    <div className="text-left">
                                        <span className="font-medium text-gray-900">Recently Checked Drivers</span>
                                        <span className="ml-2 text-sm text-gray-500">
                                            ({recentlyCheckedDrivers.length})
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-xs text-gray-500">
                                        {recentlyCheckedValidCount} valid, {recentlyCheckedInvalidCount} with issues
                                        {selectedRecentDrivers.size > 0 && (
                                            <span className="ml-1 text-blue-600 font-medium">
                                                • {selectedRecentDrivers.size} selected
                                            </span>
                                        )}
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectAll();
                                        }}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50"
                                    >
                                        {selectedRecentDrivers.size === recentlyCheckedDrivers.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                            </button>

                            {/* Expandable Content */}
                            {recentlyCheckedExpanded && (
                                <>
                                    <div className="overflow-x-auto border-t border-gray-200">
                                        <table className="min-w-full divide-y divide-gray-100">
                                            <thead className="border-b border-gray-100 bg-gray-50/95">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Include
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Driver Name
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Licence Number
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Licence Status
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Checked
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {paginatedRecentlyChecked.map((driver) => (
                                                    <tr key={driver.id} className="hover:bg-gray-50/60 transition-colors">
                                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedRecentDrivers.has(driver.id)}
                                                                onChange={() => handleToggleDriver(driver.id)}
                                                                className="h-4 w-4 border-gray-300 rounded accent-[#69BE28]"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {driver.firstName} {driver.lastName}
                                                        </td>
                                                        <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-600 font-mono">
                                                            {driver.licenceNumber}
                                                        </td>
                                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                                            <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                                <span className={`h-4 w-[3px] rounded-full ${driver.licenceValid ? 'bg-green-500' : 'bg-red-500'}`} aria-hidden="true" />
                                                                {driver.licenceStatus || (driver.licenceValid ? 'Valid' : 'Invalid')}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500">
                                                            <div className="flex items-center">
                                                                <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                                                                {driver.daysSinceLastCheck} {driver.daysSinceLastCheck === 1 ? 'day' : 'days'} ago
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <PaginationControls
                                        currentPage={recentlyCheckedPage}
                                        totalPages={recentlyCheckedTotalPages}
                                        onPageChange={setRecentlyCheckedPage}
                                        totalItems={recentlyCheckedDrivers.length}
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {/* Drivers Needing Check Section - Collapsible Accordion */}
                    {driversNeedingCheck.length > 0 && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Accordion Header */}
                            <button
                                onClick={() => setReadyToCheckExpanded(!readyToCheckExpanded)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <div className="flex items-center space-x-3">
                                    {readyToCheckExpanded ? (
                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                    ) : (
                                        <ChevronRight className="w-5 h-5 text-gray-500" />
                                    )}
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#69BE28' }}></div>
                                    <div className="text-left">
                                        <span className="font-medium text-gray-900">Drivers Ready to Check</span>
                                        <span className="ml-2 text-sm text-gray-500">
                                            ({driversNeedingCheck.length})
                                        </span>
                                    </div>
                                </div>
                                <span className="text-xs text-gray-500">
                                    {readyToCheckNeverChecked > 0 && `${readyToCheckNeverChecked} never checked`}
                                    {readyToCheckNeverChecked > 0 && readyToCheckFailed > 0 && ', '}
                                    {readyToCheckFailed > 0 && `${readyToCheckFailed} previous check failed`}
                                    {readyToCheckNeverChecked === 0 && readyToCheckFailed === 0 && 'All due for routine check'}
                                </span>
                            </button>

                            {/* Expandable Content */}
                            {readyToCheckExpanded && (
                                <>
                                    <div className="overflow-x-auto border-t border-gray-200">
                                        <table className="min-w-full divide-y divide-gray-100">
                                            <thead className="border-b border-gray-100 bg-gray-50/95">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Driver Name
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Licence Number
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Last Check
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {paginatedReadyToCheck.map((driver) => (
                                                    <tr key={driver.id} className="hover:bg-gray-50/60 transition-colors">
                                                        <td className="px-4 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {driver.firstName} {driver.lastName}
                                                        </td>
                                                        <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-600 font-mono">
                                                            {driver.licenceNumber}
                                                        </td>
                                                        <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500">
                                                            {driver.lastCheckDate ? formatDate(driver.lastCheckDate) : 'Never'}
                                                        </td>
                                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                                            {driver.lastCheckDate ? (
                                                                driver.checkSucceeded === false ? (
                                                                    <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                                        <span className="h-4 w-[3px] rounded-full bg-red-500" aria-hidden="true" />
                                                                        Check Failed
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-xs text-gray-500">
                                                                        {driver.daysSinceLastCheck}+ days ago
                                                                    </span>
                                                                )
                                                            ) : (
                                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                                    <span className="h-4 w-[3px] rounded-full bg-gray-300" aria-hidden="true" />
                                                                    Never Checked
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <PaginationControls
                                        currentPage={readyToCheckPage}
                                        totalPages={readyToCheckTotalPages}
                                        onPageChange={setReadyToCheckPage}
                                        totalItems={driversNeedingCheck.length}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">{totalDriversToCheck}</span> driver{totalDriversToCheck !== 1 ? 's' : ''} will be checked
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleProceed}
                            disabled={totalDriversToCheck === 0}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            style={{
                                backgroundColor: '#69BE28',
                                '--tw-ring-color': 'rgba(105, 190, 40, 0.5)'
                            } as React.CSSProperties}
                        >
                            Proceed with {totalDriversToCheck} Driver{totalDriversToCheck !== 1 ? 's' : ''}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecentCheckWarningModal;
